/**
 * Track Manifest
 *
 * Defines all available audio tracks organized by bus type.
 * Ambience and SFX URLs point to /audio/ in the public directory; mood music
 * streams from Supabase Storage (see bundledAudioUrl).
 *
 * Ambience and SFX are CC0 / public domain placeholders from OpenGameArt.org.
 */

// ─── Mood Music ────────────────────────────────────────────────────────

/**
 * Where a music track came from (ISS-0183). Only `bundled` exists today;
 * `generated` (ISS-0184, Pro AI music) and `byo` (ISS-0185) are here so those
 * tracks ride the same player and broadcast path when they land.
 */
export type MusicTrackSource = 'bundled' | 'generated' | 'byo';

export interface MusicTrack {
  /** `mood:slug` for bundled tracks. */
  id: string;
  moodId: string;
  title: string;
  artist?: string;
  license?: string;      // e.g. 'CC0', 'CC-BY 4.0'
  /** Credit line the app must show. Set it for any CC-BY track. */
  attribution?: string;
  /** Where the track was published, linked from the credits. */
  sourcePage?: string;
  /**
   * Length from the catalogue, for the track list before the file has loaded.
   * The player itself reads the real duration from the element.
   */
  durationSec?: number;
  url: string;
  source: MusicTrackSource;
}

export interface MoodDef {
  id: string;
  label: string;
  color: string;   // UI button color
}

/**
 * Resolve a bundled music file to a URL.
 *
 * Music does not ship in the repo: it lives in the public `machina-audio`
 * Storage bucket at the same relative paths. VITE_AUDIO_BASE_URL overrides the
 * base (e.g. `/audio/` against a local copy). Without either, fall back to
 * `/audio/` so tests and a misconfigured build still produce a sane path.
 */
export function bundledAudioUrl(path: string): string {
  const env: Partial<ImportMetaEnv> = import.meta.env ?? {};
  const base =
    env.VITE_AUDIO_BASE_URL ||
    (env.VITE_SUPABASE_URL
      ? `${env.VITE_SUPABASE_URL.replace(/\/+$/, '')}/storage/v1/object/public/machina-audio/`
      : '/audio/');
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/** Display order on the Foley Board, roughly calm to grim. */
export const MOODS: MoodDef[] = [
  { id: 'exploration', label: 'Exploration', color: '#4a90d9' },
  { id: 'travel',      label: 'Travel',      color: '#6b8e23' },
  { id: 'city',        label: 'City',        color: '#b87333' },
  { id: 'tavern',      label: 'Tavern',      color: '#d4a017' },
  { id: 'mystery',     label: 'Mystery',     color: '#8b5cf6' },
  { id: 'tension',     label: 'Tension',     color: '#dc143c' },
  { id: 'combat',      label: 'Combat',      color: '#e25822' },
  { id: 'boss',        label: 'Boss',        color: '#b91c1c' },
  { id: 'triumph',     label: 'Triumph',     color: '#22c55e' },
  { id: 'sorrow',      label: 'Sorrow',      color: '#6b7280' },
  { id: 'dread',       label: 'Dread',       color: '#1e1b4b' },
];

type BundledEntry = Omit<MusicTrack, 'moodId' | 'url' | 'source'> & { file: string };

/** The mood is the id's prefix, so an entry cannot land in the wrong playlist. */
function bundled({ file, ...entry }: BundledEntry): MusicTrack {
  return {
    ...entry,
    moodId: entry.id.slice(0, entry.id.indexOf(':')),
    url: bundledAudioUrl(file),
    source: 'bundled',
  };
}

/**
 * The free library (ISS-0183): 44 CC-BY tracks Ben approved, as
 * loudness-matched 128k mp3s. Every one needs its credit shown in the app;
 * see MUSIC_CREDITS.
 */
export const MUSIC_TRACKS: MusicTrack[] = [
  // Exploration
  bundled({
    id: 'exploration:path-through-the-mountains',
    title: 'Path Through The Mountains',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Path Through The Mountains' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/path-through-the-mountains/',
    file: 'music/exploration/path-through-the-mountains.mp3',
    durationSec: 321,
  }),
  bundled({
    id: 'exploration:passage-of-time',
    title: 'Passage of Time',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Passage Of Time' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/passage-of-time/',
    file: 'music/exploration/passage-of-time.mp3',
    durationSec: 271,
  }),
  bundled({
    id: 'exploration:dark-quest',
    title: 'Dark Quest',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 4.0',
    attribution: '"Dark Quest" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 4.0 (https://creativecommons.org/licenses/by/4.0/). Source: https://opengameart.org/content/dark-quest',
    sourcePage: 'https://opengameart.org/content/dark-quest',
    file: 'music/exploration/dark-quest.mp3',
    durationSec: 261,
  }),
  bundled({
    id: 'exploration:leshy',
    title: 'Leshy',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 4.0',
    attribution: '"Leshy" by Alexandr Zhelanov (https://www.youtube.com/c/AlexandrZhelanovsMusic), licensed CC-BY 4.0 (https://creativecommons.org/licenses/by/4.0/). Source: https://opengameart.org/content/leshy',
    sourcePage: 'https://opengameart.org/content/leshy',
    file: 'music/exploration/leshy.mp3',
    durationSec: 181,
  }),
  bundled({
    id: 'exploration:the-pyre',
    title: 'The Pyre',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"The Pyre" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:The_Pyre_(ISRC_USUAN1100846).mp3',
    file: 'music/exploration/the-pyre.mp3',
    durationSec: 233,
  }),
  bundled({
    id: 'exploration:lost-frontier',
    title: 'Lost Frontier',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Lost Frontier" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Lost_Frontier_(ISRC_USUAN1300039).mp3',
    file: 'music/exploration/lost-frontier.mp3',
    durationSec: 265,
  }),

  // Travel
  bundled({
    id: 'travel:into-the-wilds',
    title: 'Into The Wilds',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Into The Wilds' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/into-the-wilds/',
    file: 'music/travel/into-the-wilds.mp3',
    durationSec: 247,
  }),
  bundled({
    id: 'travel:path-to-lake-land',
    title: 'Path to Lake Land',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 3.0',
    attribution: '"Path to Lake Land" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 3.0 (https://creativecommons.org/licenses/by/3.0/). Source: https://opengameart.org/content/path-to-lake-land',
    sourcePage: 'https://opengameart.org/content/path-to-lake-land',
    file: 'music/travel/path-to-lake-land.mp3',
    durationSec: 255,
  }),
  bundled({
    id: 'travel:journey-to-the-east-rocks',
    title: 'Journey to the East Rocks',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 3.0',
    attribution: '"Journey to the East Rocks" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 3.0 (https://creativecommons.org/licenses/by/3.0/). Source: https://opengameart.org/content/journey-to-the-east-rocks',
    sourcePage: 'https://opengameart.org/content/journey-to-the-east-rocks',
    file: 'music/travel/journey-to-the-east-rocks.mp3',
    durationSec: 287,
  }),

  // City
  bundled({
    id: 'city:balefire',
    title: 'Balefire',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Balefire' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/balefire/',
    file: 'music/city/balefire.mp3',
    durationSec: 340,
  }),
  bundled({
    id: 'city:time-to-buy-a-new-sword',
    title: 'Time to buy a new sword',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 3.0',
    attribution: '"Time to buy a new sword" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 3.0 (https://creativecommons.org/licenses/by/3.0/). Source: https://opengameart.org/content/time-to-buy-a-new-sword',
    sourcePage: 'https://opengameart.org/content/time-to-buy-a-new-sword',
    file: 'music/city/time-to-buy-a-new-sword.mp3',
    durationSec: 139,
  }),
  bundled({
    id: 'city:teller-of-the-tales',
    title: 'Teller of the Tales',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Teller of the Tales" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Teller_of_the_Tales_(ISRC_USUAN1400020).mp3',
    file: 'music/city/teller-of-the-tales.mp3',
    durationSec: 212,
  }),

  // Tavern
  bundled({
    id: 'tavern:three-sheets-to-the-wind',
    title: 'Three Sheets to the Wind',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Three Sheets To The Wind' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/three-sheets-to-the-wind/',
    file: 'music/tavern/three-sheets-to-the-wind.mp3',
    durationSec: 200,
  }),
  bundled({
    id: 'tavern:pippin-the-hunchback',
    title: 'Pippin the Hunchback',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Pippin the Hunchback" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Pippin_the_Hunchback_(ISRC_USUAN1400005).mp3',
    file: 'music/tavern/pippin-the-hunchback.mp3',
    durationSec: 193,
  }),
  bundled({
    id: 'tavern:achaidh-cheide',
    title: 'Achaidh Cheide',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Achaidh Cheide" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Achaidh_Cheide_(ISRC_USUAN1100340).mp3',
    file: 'music/tavern/achaidh-cheide.mp3',
    durationSec: 134,
  }),
  bundled({
    id: 'tavern:suonatore-di-liuto',
    title: 'Suonatore di Liuto',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Suonatore di Liuto" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Suonatore_di_Liuto_(ISRC_USUAN1400023).mp3',
    file: 'music/tavern/suonatore-di-liuto.mp3',
    durationSec: 144,
  }),

  // Mystery
  bundled({
    id: 'mystery:in-search-of-solitude',
    title: 'In Search of Solitude',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'In Search Of Solitude' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/in-search-of-solitude/',
    file: 'music/mystery/in-search-of-solitude.mp3',
    durationSec: 445,
  }),
  bundled({
    id: 'mystery:the-illusionist',
    title: 'The Illusionist',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'The Illusionist' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/the-illusionist/',
    file: 'music/mystery/the-illusionist.mp3',
    durationSec: 215,
  }),
  bundled({
    id: 'mystery:mystery-forest',
    title: 'Mystery Forest',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 3.0',
    attribution: '"Mystery Forest" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 3.0 (https://creativecommons.org/licenses/by/3.0/). Source: https://opengameart.org/content/mystery-forest',
    sourcePage: 'https://opengameart.org/content/mystery-forest',
    file: 'music/mystery/mystery-forest.mp3',
    durationSec: 232,
  }),
  bundled({
    id: 'mystery:lightless-dawn',
    title: 'Lightless Dawn',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Lightless Dawn" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Lightless_Dawn_(ISRC_USUAN1100655).mp3',
    file: 'music/mystery/lightless-dawn.mp3',
    durationSec: 380,
  }),

  // Tension
  bundled({
    id: 'tension:sanctuary',
    title: 'Sanctuary',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Sanctuary' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/sanctuary/',
    file: 'music/tension/sanctuary.mp3',
    durationSec: 246,
  }),
  bundled({
    id: 'tension:anabasis-i',
    title: 'Anabasis I',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Anabasis I' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/anabasis-i/',
    file: 'music/tension/anabasis-i.mp3',
    durationSec: 561,
  }),
  bundled({
    id: 'tension:descent',
    title: 'Descent',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Descent' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/descent/',
    file: 'music/tension/descent.mp3',
    durationSec: 138,
  }),
  bundled({
    id: 'tension:darkest-child',
    title: 'Darkest Child',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Darkest Child" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Darkest_Child_(ISRC_USUAN1100783).mp3',
    file: 'music/tension/darkest-child.mp3',
    durationSec: 239,
  }),
  bundled({
    id: 'tension:interloper',
    title: 'Interloper',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Interloper" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Interloper_(ISRC_USUAN1100401).mp3',
    file: 'music/tension/interloper.mp3',
    durationSec: 263,
  }),
  bundled({
    id: 'tension:oppressive-gloom',
    title: 'Oppressive Gloom',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Oppressive Gloom" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Oppressive_Gloom_(ISRC_USUAN1100885).mp3',
    file: 'music/tension/oppressive-gloom.mp3',
    durationSec: 199,
  }),

  // Combat
  bundled({
    id: 'combat:juggernaut',
    title: 'Juggernaut',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Juggernaut' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/juggernaut/',
    file: 'music/combat/juggernaut.mp3',
    durationSec: 222,
  }),
  bundled({
    id: 'combat:legionnaire-2022-remaster',
    title: 'Legionnaire (2022 Remaster)',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Legionnaire' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/legionnaire-2022/',
    file: 'music/combat/legionnaire-2022-remaster.mp3',
    durationSec: 187,
  }),
  bundled({
    id: 'combat:five-armies',
    title: 'Five Armies',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Five Armies" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Five_Armies_(ISRC_USUAN1100875).mp3',
    file: 'music/combat/five-armies.mp3',
    durationSec: 156,
  }),
  bundled({
    id: 'combat:clash-defiant',
    title: 'Clash Defiant',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Clash Defiant" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Clash_Defiant_(ISRC_USUAN1600003).mp3',
    file: 'music/combat/clash-defiant.mp3',
    durationSec: 376,
  }),

  // Boss
  bundled({
    id: 'boss:darkness-march',
    title: 'Darkness March',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 4.0',
    attribution: '"Darkness March" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 4.0 (https://creativecommons.org/licenses/by/4.0/). Source: https://opengameart.org/content/darkness-march',
    sourcePage: 'https://opengameart.org/content/darkness-march',
    file: 'music/boss/darkness-march.mp3',
    durationSec: 149,
  }),
  bundled({
    id: 'boss:demonic-onslaught',
    title: 'Demonic Onslaught',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 4.0',
    attribution: '"Demonic Onslaught" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 4.0 (https://creativecommons.org/licenses/by/4.0/). Source: https://opengameart.org/content/demonic-onslaught',
    sourcePage: 'https://opengameart.org/content/demonic-onslaught',
    file: 'music/boss/demonic-onslaught.mp3',
    durationSec: 180,
  }),

  // Triumph
  bundled({
    id: 'triumph:age-of-wonder',
    title: 'Age of Wonder',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Age of Wonder' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/age-of-wonder/',
    file: 'music/triumph/age-of-wonder.mp3',
    durationSec: 351,
  }),
  bundled({
    id: 'triumph:long-road-ahead',
    title: 'Long Road Ahead',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Long Road Ahead" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Long_Road_Ahead_(ISRC_USUAN1100588).mp3',
    file: 'music/triumph/long-road-ahead.mp3',
    durationSec: 147,
  }),
  bundled({
    id: 'triumph:shoulders-of-giants',
    title: 'Shoulders of Giants',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Shoulders Of Giants' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/shoulders-of-giants/',
    file: 'music/triumph/shoulders-of-giants.mp3',
    durationSec: 443,
  }),
  bundled({
    id: 'triumph:where-stars-fall',
    title: 'Where Stars Fall',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Where Stars Fall' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/where-stars-fall/',
    file: 'music/triumph/where-stars-fall.mp3',
    durationSec: 271,
  }),

  // Sorrow
  bundled({
    id: 'sorrow:the-long-dark',
    title: 'The Long Dark',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'The Long Dark' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/the-long-dark/',
    file: 'music/sorrow/the-long-dark.mp3',
    durationSec: 440,
  }),
  bundled({
    id: 'sorrow:caves-of-sorrow',
    title: 'Caves of Sorrow',
    artist: 'Alexandr Zhelanov',
    license: 'CC-BY 3.0',
    attribution: '"Caves of Sorrow" by Alexandr Zhelanov (https://soundcloud.com/alexandr-zhelanov), licensed CC-BY 3.0 (https://creativecommons.org/licenses/by/3.0/). Source: https://opengameart.org/content/caves-of-sorrow',
    sourcePage: 'https://opengameart.org/content/caves-of-sorrow',
    file: 'music/sorrow/caves-of-sorrow.mp3',
    durationSec: 283,
  }),
  bundled({
    id: 'sorrow:dark-times',
    title: 'Dark Times',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Dark Times" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Dark_Times_(ISRC_USUAN1100747).mp3',
    file: 'music/sorrow/dark-times.mp3',
    durationSec: 184,
  }),

  // Dread
  bundled({
    id: 'dread:the-old-ones',
    title: 'The Old Ones',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'The Old Ones' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/the-old-ones/',
    file: 'music/dread/the-old-ones.mp3',
    durationSec: 302,
  }),
  bundled({
    id: 'dread:maleficus',
    title: 'Maleficus',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Maleficus' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/maleficus/',
    file: 'music/dread/maleficus.mp3',
    durationSec: 385,
  }),
  bundled({
    id: 'dread:katabasis-i',
    title: 'Katabasis I',
    artist: 'Scott Buckley',
    license: 'CC-BY 4.0',
    attribution: "'Katabasis I' by Scott Buckley - released under CC-BY 4.0. www.scottbuckley.com.au",
    sourcePage: 'https://www.scottbuckley.com.au/library/katabasis-i/',
    file: 'music/dread/katabasis-i.mp3',
    durationSec: 590,
  }),
  bundled({
    id: 'dread:halls-of-the-undead',
    title: 'Halls of the Undead',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Halls of the Undead" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Halls_of_the_Undead_(ISRC_USUAN1100355).mp3',
    file: 'music/dread/halls-of-the-undead.mp3',
    durationSec: 293,
  }),
  bundled({
    id: 'dread:unseen-horrors',
    title: 'Unseen Horrors',
    artist: 'Kevin MacLeod',
    license: 'CC-BY 3.0',
    attribution: '"Unseen Horrors" Kevin MacLeod (incompetech.com). Licensed under Creative Commons: By Attribution 3.0 License. http://creativecommons.org/licenses/by/3.0/',
    sourcePage: 'https://commons.wikimedia.org/wiki/File:Unseen_Horrors_(ISRC_USUAN1400043).mp3',
    file: 'music/dread/unseen-horrors.mp3',
    durationSec: 251,
  }),
];

/** A mood's playlist, in manifest order. */
export function moodPlaylist(moodId: string): MusicTrack[] {
  return MUSIC_TRACKS.filter((t) => t.moodId === moodId);
}

/** Tracks whose license asks for a credit in the app (CC-BY). */
export const MUSIC_CREDITS: MusicTrack[] = MUSIC_TRACKS.filter((t) => !!t.attribution);

// ─── Ambience Layers ───────────────────────────────────────────────────

export interface AmbienceLayer {
  id: string;
  label: string;
  url: string;
  icon: string;      // emoji for the tap button
  category: 'nature' | 'urban' | 'dungeon' | 'grist';
}

export const AMBIENCE_LAYERS: AmbienceLayer[] = [
  // Nature
  { id: 'rain',       label: 'Rain',         url: '/audio/ambience/rain.mp3',          icon: '🌧', category: 'nature' },
  { id: 'wind',       label: 'Wind',         url: '/audio/ambience/wind.ogg',          icon: '💨', category: 'nature' },
  { id: 'forest',     label: 'Forest',       url: '/audio/ambience/forest.mp3',        icon: '🌲', category: 'nature' },
  { id: 'night',      label: 'Night',        url: '/audio/ambience/night.mp3',         icon: '🌙', category: 'nature' },

  // Urban
  { id: 'crowd',      label: 'Crowd',        url: '/audio/ambience/crowd.mp3',         icon: '👥', category: 'urban' },
  { id: 'tavern-amb', label: 'Tavern',       url: '/audio/ambience/tavern.ogg',        icon: '🍺', category: 'urban' },
  { id: 'harbor',     label: 'Harbor',       url: '/audio/ambience/harbor.ogg',        icon: '⚓', category: 'urban' },

  // Dungeon
  { id: 'dungeon',    label: 'Dungeon',      url: '/audio/ambience/dungeon.ogg',       icon: '🕯', category: 'dungeon' },
  { id: 'cave',       label: 'Cave',         url: '/audio/ambience/cave.flac',         icon: '🦇', category: 'dungeon' },

  // Grist-specific
  { id: 'tether-hum', label: 'Tether Hum',   url: '/audio/ambience/tether-hum.ogg',    icon: '⚡', category: 'grist' },
  { id: 'scar-whisp', label: 'Scar Whispers',url: '/audio/ambience/scar-whispers.ogg', icon: '🌀', category: 'grist' },
  { id: 'foundry',    label: 'Foundry',      url: '/audio/ambience/foundry.ogg',       icon: '🔨', category: 'grist' },
];

// ─── Sound Effects ─────────────────────────────────────────────────────

export interface SFXEntry {
  id: string;
  label: string;
  url: string;
  icon: string;      // emoji for the tap button
  category: 'combat' | 'environment' | 'social' | 'magic';
}

export const SFX_ENTRIES: SFXEntry[] = [
  // Combat
  { id: 'sword-clash',  label: 'Clash',    url: '/audio/sfx/sword-clash.ogg',    icon: '⚔️',  category: 'combat' },
  { id: 'arrow-loose',  label: 'Arrow',    url: '/audio/sfx/arrow-loose.ogg',    icon: '🏹',  category: 'combat' },
  { id: 'shield-block', label: 'Shield',   url: '/audio/sfx/shield-block.ogg',   icon: '🛡',  category: 'combat' },
  { id: 'critical-hit', label: 'Crit!',    url: '/audio/sfx/critical-hit.ogg',   icon: '💥',  category: 'combat' },
  { id: 'death-cry',    label: 'Death',    url: '/audio/sfx/death-cry.ogg',      icon: '💀',  category: 'combat' },

  // Environment
  { id: 'door-creak',   label: 'Door',     url: '/audio/sfx/door-creak.ogg',     icon: '🚪',  category: 'environment' },
  { id: 'chest-open',   label: 'Chest',    url: '/audio/sfx/chest-open.ogg',     icon: '📦',  category: 'environment' },
  { id: 'trap-spring',  label: 'Trap',     url: '/audio/sfx/trap-spring.ogg',    icon: '⚙️',  category: 'environment' },

  // Social
  { id: 'coin-purse',   label: 'Coins',    url: '/audio/sfx/coin-purse.ogg',     icon: '💰',  category: 'social' },
  { id: 'crowd-gasp',   label: 'Gasp',     url: '/audio/sfx/crowd-gasp.wav',     icon: '😮',  category: 'social' },
  { id: 'dramatic-sting',label: 'Sting',   url: '/audio/sfx/dramatic-sting.ogg', icon: '🎵',  category: 'social' },

  // Magic
  { id: 'arcane-hum',   label: 'Arcane',   url: '/audio/sfx/arcane-hum.ogg',     icon: '✨',  category: 'magic' },
  { id: 'divine-chime', label: 'Divine',   url: '/audio/sfx/divine-chime.ogg',   icon: '🔔',  category: 'magic' },
  { id: 'fireball',     label: 'Fireball', url: '/audio/sfx/fireball.ogg',       icon: '🔥',  category: 'magic' },
  { id: 'teleport',     label: 'Teleport', url: '/audio/sfx/teleport.ogg',       icon: '🌀',  category: 'magic' },
];
